import type { TokenRequest } from "ably"
import type { EntityChange } from "./model"
import { Operation } from "./model"
import type { Pages } from "./newestFirstPages"
import {
  patchRow,
  placeOf,
  removeRow,
  settleNewerPage,
  spliceRow,
} from "./newestFirstPages"
import subscribe from "./subscribe"
import type { UpdateableEntity, Uuid } from "@/utils"

/** Gathers the inserts of a burst into one fetch. */
const NEWER_DELAY_MS = 300
/** How often to check whether a fetch in flight has landed. */
const BUSY_RETRY_MS = 250

type Recipe<TItem> = (pages: Pages<TItem>) => void

type Args<TItem extends UpdateableEntity> = {
  channel: string
  event: string
  getAblyToken: () => Promise<TokenRequest>
  /** Whether a change concerns this list at all, e.g. a run of the same prompt. */
  isRelevant: (row: TItem) => boolean
  /** Whether the server would list a row, for one spliced in without asking it. */
  isVisible: (row: TItem, actorId: Uuid | null) => boolean
  /** The cached pages, and whether any fetch of them is in flight. */
  getCache: () => { pages: Pages<TItem> | undefined; isFetching: boolean }
  update: (recipe: Recipe<TItem>) => void
  /** Fetches the page before the first one; resolves, never rejects, once it has settled. */
  fetchNewer: () => PromiseLike<unknown>
  /** Refetches the first page; resolves, never rejects. */
  refetchHead: () => PromiseLike<unknown>
  removed: Promise<unknown>
}

/**
 * Keeps an infinite list of newest-first, UUIDv7-ordered rows live from the
 * realtime channel, for an RTK Query `onCacheEntryAdded`. Resolves once the
 * cache entry is removed.
 */
export default async function syncNewestFirstPages<
  TItem extends UpdateableEntity,
>({
  channel,
  event,
  getAblyToken,
  isRelevant,
  isVisible,
  getCache,
  update,
  fetchNewer,
  refetchHead,
  removed,
}: Args<TItem>) {
  let newerTimer: ReturnType<typeof setTimeout> | undefined
  let replayTimer: ReturnType<typeof setTimeout> | undefined
  // A fetch lands on the pages as they were when it started, undoing changes
  // made meanwhile, so those are kept to apply again.
  let replay: Recipe<TItem>[] = []
  let isRemoved = false
  // Read through a call, as a check made before an await would otherwise hold after it.
  const wasRemoved = () => isRemoved

  const flushReplay = () => {
    const recipes = replay
    replay = []
    if (recipes.length) {
      update((pages) => {
        for (const recipe of recipes) {
          recipe(pages)
        }
      })
    }
  }

  const replayWhenIdle = () => {
    replayTimer = undefined
    if (isRemoved) {
      return
    }
    if (getCache().isFetching) {
      replayTimer = setTimeout(replayWhenIdle, BUSY_RETRY_MS)

      return
    }

    flushReplay()
  }

  const apply = (recipe: Recipe<TItem>) => {
    update(recipe)
    if (getCache().isFetching) {
      replay.push(recipe)
      replayTimer ??= setTimeout(replayWhenIdle, BUSY_RETRY_MS)
    }
  }

  const loadNewer = async (): Promise<void> => {
    if (wasRemoved()) {
      return
    }
    const { pages, isFetching } = getCache()
    if (isFetching) {
      scheduleNewer(BUSY_RETRY_MS)

      return
    }

    // An empty list has no cursor to go back from.
    if (!pages?.pages[0]?.prevCursor) {
      await refetchHead()

      return
    }

    const pageCount = pages.pages.length
    await fetchNewer()
    const landed = getCache().pages
    // A failed fetch prepends nothing; the next insert tries again.
    if (wasRemoved() || landed?.pages.length !== pageCount + 1) {
      return
    }

    flushReplay()
    const hasPrev = landed.pages[0]?.hasPrev
    update(settleNewerPage)
    // More than a page arrived: keep going until the head.
    if (hasPrev) {
      await loadNewer()
    }
  }

  const scheduleNewer = (delay: number) => {
    if (!newerTimer && !isRemoved) {
      newerTimer = setTimeout(() => {
        newerTimer = undefined
        void loadNewer()
      }, delay)
    }
  }

  const unbind = await subscribe(channel, getAblyToken, {
    [event]: (change: EntityChange<TItem>) => {
      switch (change.operation) {
        case Operation.Update:
          if (isRelevant(change.payload)) {
            apply((pages) => {
              patchRow(pages, change.payload)
            })
          }

          break
        case Operation.Delete:
          apply((pages) => {
            removeRow(pages, change.payload)
          })

          break
        case Operation.Insert: {
          const row = change.payload
          if (!isRelevant(row)) {
            break
          }

          const { pages } = getCache()
          const place = pages ? placeOf(pages, row) : "empty"
          if (place === "empty" || place === "newer") {
            scheduleNewer(NEWER_DELAY_MS)
          } else if (
            (place === "between" || place === "older") &&
            isVisible(row, change.actorId)
          ) {
            apply((current) => {
              spliceRow(current, row)
            })
          }

          break
        }
      }
    },
  })

  await removed
  isRemoved = true
  clearTimeout(newerTimer)
  clearTimeout(replayTimer)
  unbind()
}
